//New Product Card

import React, { useEffect, useState } from "react";
import { Button } from "react-bootstrap";
import VerifiedIcon from "@mui/icons-material/Verified";
import Chip from "@mui/material/Chip";
import { Box, Grid, Skeleton, Tooltip, Typography } from "@mui/material";

import defaultProductImage from "../../assets/defaultImage.png";
import theme from "../../styles/theme/theme";
import PlaceBidModal from "./PlaceBidModal";
import BuyConfirmModal from "./BuyConfirmModal";
import {
  FaClipboardList,
  FaGavel,
  FaMapPin,
  FaShoppingCart,
} from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { capitalizeFirst } from "../../utils/stringUtils";
import { formatCurrency, formatPrice } from "../../utils/price";

const ProductCard = ({
  product,
  onViewDetails,
  hideButtons = false,
  hideStock = false,
  clampDescription = false,
  customHeight = null,
  viewType = "grid",
  isLoggedIn,
}) => {
  const navigate = useNavigate();
  const [showModal, setShowModal] = useState(false);
  const [showBuyModal, setShowBuyModal] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [isLoading, setIsLoading] = useState(true); // New state for loading
  // console.log("Products:", product);
  if (product?.title) {
    product.name = product.title;
  }

  // Effect to handle image loading
  useEffect(() => {
    // Show skeleton until the product data is loaded
    if (!product) {
      setIsLoading(true);
      return;
    }

    // If there's an image, wait for it to load; else, still show skeleton briefly
    if (product.image_url) {
      const img = new Image();
      img.src = product.image_url;
      img.onload = () => setIsLoading(false);
      img.onerror = () => setIsLoading(false);
    } else {
      // Delay a tiny bit to ensure skeleton shows even for default image
      const timer = setTimeout(() => setIsLoading(false), 50);
      return () => clearTimeout(timer);
    }
  }, [product]);

  const handleProductClick = () => {
    if (isLoggedIn) {
      navigate(`/product/${product.id}`);
    } else {
      navigate("/login");
    }
  };

  // Skeleton View
  if (isLoading || !viewType) {
    return (
      <>
        {viewType === "list" ? (
          // LIST VIEW SKELETON
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 2,
              borderRadius: 3,
              overflow: "hidden",
              bgcolor: "rgba(255, 255, 255, 0.95)",
              backdropFilter: "blur(12px)",
              border: "1px solid rgba(0,0,0,0.04)",
              width: "100%",
              minHeight: 130,
              transition: "all 0.4s ease",
              cursor: "pointer",
              p: 1.5,
            }}
          >
            {/* Image */}
            <Skeleton
              variant="rectangular"
              width={90}
              height={90}
              sx={{ borderRadius: 2, flexShrink: 0 }}
            />

            {/* Details Section */}
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns:
                  "200px 80px 120px 150px 120px 100px 50px 120px",
                alignItems: "center",
                flex: 1,
                gap: 2,
              }}
            >
              {/* Name */}
              <Skeleton variant="text" width="80%" height={20} />

              {/* Category */}
              <Skeleton
                variant="rectangular"
                width={70}
                height={22}
                sx={{ borderRadius: 1 }}
              />

              {/* Price */}
              <Skeleton
                variant="rectangular"
                width={60}
                height={22}
                sx={{ borderRadius: 1 }}
              />

              {/* Company */}
              <Skeleton
                variant="rectangular"
                width={90}
                height={22}
                sx={{ borderRadius: 1 }}
              />

              {/* Location */}
              <Skeleton
                variant="rectangular"
                width={90}
                height={22}
                sx={{ borderRadius: 1 }}
              />

              {/* Stock */}
              <Skeleton
                variant="rectangular"
                width={60}
                height={22}
                sx={{ borderRadius: 1 }}
              />

              {/* Verified */}
              <Skeleton
                variant="circular"
                width={24}
                height={24}
                sx={{ borderRadius: "50%" }}
              />

              {/* Buttons */}
              <Box sx={{ display: "flex", gap: 1 }}>
                <Skeleton variant="rectangular" width={80} height={28} />
                <Skeleton variant="rectangular" width={80} height={28} />
              </Box>
            </Box>
          </Box>
        ) : (
          // GRID VIEW SKELETON
          <Box
            sx={{
              borderRadius: 3,
              width: { xs: 300, md: 240 },
              overflow: "hidden",
              bgcolor: "rgba(255, 255, 255, 0.95)",
              backdropFilter: "blur(12px)",
              border: "1px solid rgba(0,0,0,0.04)",
              height: customHeight || 300,
              transition: "all 0.4s ease",
              cursor: "pointer",
            }}
          >
            {/* Product Image */}
            <Skeleton variant="rectangular" width="100%" height="35%" />

            {/* Details */}
            <Box px={2} py={2} display="flex" flexDirection="column" gap={1}>
              {/* Name */}
              <Skeleton variant="text" width="80%" height={22} />

              <Box sx={{ height: 6 }} />

              {/* Price */}
              <Skeleton
                variant="rectangular"
                width={100}
                height={22}
                sx={{ borderRadius: 2 }}
              />

              {/* Blue Badge */}
              <Skeleton
                variant="rectangular"
                width={100}
                height={20}
                sx={{ borderRadius: 1.5 }}
              />

              {/* Yellow Box */}
              <Skeleton
                variant="rectangular"
                width={100}
                height={22}
                sx={{ borderRadius: 1.5 }}
              />

              {/* Gradient Chip */}
              <Skeleton
                variant="rectangular"
                width={100}
                height={28}
                sx={{ borderRadius: 16 }}
              />
            </Box>
          </Box>
        )}
      </>
    );
  }

  // GRID VIEW DESIGN
  const gridView = (
    <Box
      sx={{
        position: "relative",
        display: "flex",
        flexDirection: "column",
        borderRadius: 3,
        width: { xs: 300, md: 240 },
        overflow: "hidden",
        bgcolor: "background.paper",
        border: "1px solid rgba(0,0,0,0.04)",
        height: customHeight || 300,
        transition:
          "transform 0.36s cubic-bezier(.2,.8,.2,1), box-shadow 0.36s",
        cursor: "pointer",
        boxShadow: isHovered
          ? "0 18px 40px rgba(15, 23, 42, 0.12)"
          : "0 6px 18px rgba(15,23,42,0.06)",
        transform: isHovered ? "translateY(-6px)" : "none",
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => {
        if (!showModal && !showBuyModal) {
          setIsHovered(false);
        }
      }}
      onClick={handleProductClick}
    >
      {/* Image area with subtle zoom + gradient */}
      <Box
        sx={{
          position: "relative",
          width: "100%",
          height: "55%",
          overflow: "hidden",
          backgroundColor: "#f6f7fa",
        }}
      >
        <Box
          component="img"
          src={product?.image_url?.trim() || defaultProductImage}
          alt={product?.name}
          loading="lazy"
          sx={{
            width: "100%",
            height: "100%",
            objectFit: "cover",
            transition: "transform 0.6s ease",
            transform: isHovered ? "scale(1.06)" : "scale(1.0)",
          }}
        />

        {/* Soft gradient to improve text contrast */}
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            background:
              "linear-gradient(180deg, rgba(0,0,0,0) 45%, rgba(0,0,0,0.32) 100%)",
            pointerEvents: "none",
          }}
        />

        {/* Category chip */}
        <Box sx={{ position: "absolute", top: 10, left: 10 }}>
          <Chip
            label={product?.category?.name || "Category"}
            size="small"
            sx={{
              background: "rgba(0,0,0,0.56)",
              color: "#fff",
              fontSize: 12,
              height: 28,
              fontWeight: 600,
              backdropFilter: "blur(6px)",
              border: "1px solid rgba(255,255,255,0.12)",
            }}
          />
        </Box>

        {/* Verified */}
        <Box sx={{ position: "absolute", top: 10, right: 10 }}>
          <Chip
            icon={
              <VerifiedIcon
                color="inherit"
                sx={{ fontSize: 16, color: "#fff" }}
              />
            }
            label="Verified"
            size="small"
            sx={{
              background: "linear-gradient(135deg,#4CAF50 0%,#388E3C 100%)",
              color: "#fff",
              fontSize: 12,
              height: 28,
              fontWeight: 600,
              boxShadow: "0 6px 18px rgba(56,142,60,0.18)",
            }}
          />
        </Box>

        {/* Stock badge (only show when out of stock)
          {product?.stock?.quantity <= 0 && (
            <Box
              sx={{
                position: "absolute",
                bottom: 8,
                left: 10,
                background: "rgba(220,38,38,0.92)",
                color: "#fff",
                px: 2,
                py: 0.5,
                borderRadius: 999,
                fontSize: 12,
                fontWeight: 700,
                letterSpacing: 0.2,
                boxShadow: "0 6px 18px rgba(220,38,38,0.12)",
              }}
            >
              Out of stock
            </Box>
          )} */}
      </Box>

      {/* Main content */}
      <Box
        px={2}
        py={1.25}
        sx={{
          transition: "filter 0.2s ease",
          filter: isHovered && !hideButtons ? "blur(1.2px)" : "none",
          flex: "1 1 auto",
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
          overflow: "hidden",
          paddingBottom: "72px",
        }}
      >
        <div>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
            <Typography
              variant="subtitle1"
              fontWeight="bold"
              color="#0f172a"
              title={product?.name}
              sx={{
                fontSize: 14,
                minWidth: 0,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {product?.name || ""}
            </Typography>

            {/* subtle divider */}
            <Box sx={{ height: 6 }} />

            {/* Price row - new line under the title with design flow */}
            {/* Price bubble left under the title */}
            <Box
              sx={{
                background: "linear-gradient(90deg,#F4C542 0%, #F2B537 100%)",
                color: "#082032",
                px: 1.5,
                py: 0.5,
                borderRadius: 2,
                fontWeight: 800,
                boxShadow: "0 8px 20px rgba(244,197,66,0.12)",
                fontSize: 13,
                minWidth: 0,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                alignSelf: "flex-start",
              }}
            >
              {/* {product?.price != null &&
                !isNaN(product.price) &&
                `A$${formatPrice(product.price)}`} */}
              {formatCurrency(product.price, product.currency)}
            </Box>
          </Box>

          <Box sx={{ height: 6 }} />

          {/* Company Name */}
          <Box
            sx={{
              display: "inline-block", // make it shrink to fit content
              background: "rgba(59, 130, 246, 0.1)",
              color: "#3B82F6",
              fontWeight: 600,
              fontSize: 12,
              px: 1.5,
              py: 0.5,
              borderRadius: 1.5,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              alignSelf: "flex-start", // ensure it stays aligned left
            }}
          >
            {product?.seller?.company_name || "N/A"}
          </Box>

          <Box sx={{ height: 6 }} />

          {/* Location */}
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 0.5,
              background: "rgba(244, 196, 66, 0.15)",
              color: "#000000",
              fontWeight: 600,
              fontSize: 12,
              px: 1.5,
              py: 0.5,
              borderRadius: 1.5,
              alignSelf: "flex-start",
              // 1. Set a max-width so the container knows when to stop growing
              maxWidth: "100%",
              overflow: "hidden",
            }}
          >
            {/* 2. flexShrink: 0 prevents the icon from disappearing */}
            <FaMapPin
              style={{ flexShrink: 0, fontSize: 12, color: "#F59E42" }}
            />

            {/* 3. Wrap the text in a span that handles the ellipsis */}
            <span
              style={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                display: "block",
              }}
            >
              {capitalizeFirst(product?.location) || "Unknown"}
            </span>
          </Box>

          {!hideStock && (
            <Box mt={0.75}>
              <Typography
                variant="body2"
                fontWeight="bold"
                sx={{
                  color: product?.stock?.quantity > 0 ? "#0f172a" : "#b91c1c",
                  fontSize: 13,
                }}
              >
                {product?.stock?.quantity > 0 ? (
                  <Chip
                    label={`Quantity: ${product.stock?.quantity} ${product.unit_of_measurement}`}
                    size="small"
                    sx={{
                      background:
                        "linear-gradient(135deg, #3B82F6 0%, #6366F1 100%)", // blue-purple gradient
                      color: "#fff",
                      fontSize: 12,
                      px: 1,
                      height: 28,
                      fontWeight: 600,
                      boxShadow: "0 6px 18px rgba(59,130,246,0.18)",
                    }}
                  />
                ) : (
                  <Chip
                    label="Out of stock"
                    size="small"
                    sx={{
                      background:
                        "linear-gradient(135deg, #EF4444 0%, #F59E42 100%)", // red to orange gradient
                      color: "#fff",
                      fontSize: 12,
                      px: 1,
                      height: 28,
                      fontWeight: 600,
                      boxShadow: "0 6px 18px rgba(239,68,68,0.18)",
                    }}
                  />
                )}
              </Typography>
            </Box>
          )}

          {product?.title && (
            <Typography
              mt={0.75}
              variant="body2"
              sx={{ color: "#6b7280", fontSize: 13 }}
              noWrap={!!clampDescription}
              title={product.title}
            >
              {product.title}
            </Typography>
          )}
        </div>
      </Box>

      {!hideButtons && product?.stock?.quantity > 0 && (
        <Box
          sx={{
            position: "absolute",
            left: 0,
            right: 0,
            bottom: 12,
            height: "auto",
            display: "flex",
            gap: 1.5,
            alignItems: "center",
            justifyContent: "center",
            flexWrap: "wrap",
            pointerEvents: isHovered ? "auto" : "none",
            transition: "opacity 0.18s ease, transform 0.28s ease",
            opacity: isHovered ? 1 : 0,
            transform: isHovered ? "translateY(0)" : "translateY(6px)",
            zIndex: 40,
            backgroundColor: "rgba(255,255,255,0.0)",
            px: { xs: 2, sm: 3, md: 4 }, // responsive horizontal padding
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <Button
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                product.allow_bidding === 1
                  ? setShowModal(true)
                  : setShowBuyModal(true);
              }}
              style={{
                backgroundColor: theme.palette.secondary.main,
                borderColor: theme.palette.secondary.main,
                color: theme.palette.dark.main,
                fontWeight: 700,
                paddingLeft: 6,
                paddingRight: 6,
                whiteSpace: "nowrap",
                display: "flex", //  align icon + text nicely
                alignItems: "center",
                gap: 6, //  space between icon & text
              }}
            >
              {product?.allow_bidding === 1 ? (
                <>
                  <FaGavel color="#000" size={14} />
                  Place Bid
                </>
              ) : (
                <>
                  <FaShoppingCart color="#000" size={14} />
                  Buy Now
                </>
              )}
            </Button>

            <Button
              size="sm"
              onClick={handleProductClick}
              style={{
                backgroundColor: theme.palette.primary.main,
                borderColor: theme.palette.primary.main,
                color: theme.palette.primary.contrastText,
                fontWeight: 700,
                paddingLeft: 6,
                display: "flex",
                paddingRight: 6,
                whiteSpace: "nowrap",
                gap: 6,
                alignItems: "center",
              }}
            >
              <>
                <FaClipboardList color="#fff" />
                View Details
              </>
            </Button>
          </div>
        </Box>
      )}
    </Box>
  );

  //LIST VIEW DESGIN
  const listView = (
    <Box
      sx={{
        display: "flex",
        flexDirection: "row",
        borderRadius: 3,
        width: "100%",
        minHeight: 100,
        overflow: "hidden",
        bgcolor: "background.paper",
        border: "1px solid rgba(0,0,0,0.04)",
        cursor: "pointer",
        boxShadow: isHovered
          ? "0 18px 40px rgba(15,23,42,0.12)"
          : "0 6px 18px rgba(15,23,42,0.06)",
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => {
        if (!showModal && !showBuyModal) setIsHovered(false);
      }}
      onClick={handleProductClick}
    >
      {/* Image */}
      <Box
        sx={{
          width: 100,
          height: 100,
          flexShrink: 0,
          position: "relative",
          backgroundColor: "#f6f7fa",
        }}
      >
        <Box
          component="img"
          src={product?.image_url?.trim() || defaultProductImage}
          alt={product?.name}
          loading="lazy"
          sx={{ width: "100%", height: "100%", objectFit: "cover" }}
        />
      </Box>

      {/* Content */}
      <Box
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "row",
          justifyContent: "space-between",
          alignItems: "center",
          p: 2,
          gap: 2,
        }}
      >
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "200px 80px 120px 150px 120px 100px 100px", // fixed column widths for table-like alignment
            gap: 2,
            alignItems: "center",
            flex: 1,
            justifyContent: "space-evenly",
          }}
        >
          {/* Product Name */}
          <Typography
            variant="subtitle1"
            fontWeight="bold"
            color="#0f172a"
            noWrap
            sx={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {product?.name}
          </Typography>

          {/* Category */}
          <Chip
            label={product?.category?.name || "Category"}
            size="small"
            sx={{
              background: "rgba(0,0,0,0.56)",
              color: "#fff",
              fontSize: 12,
              height: 28,
              fontWeight: 600,
              backdropFilter: "blur(6px)",
              border: "1px solid rgba(255,255,255,0.12)",
              width: "auto", // ensures it wraps content
              maxWidth: 140, // optional: prevents extremely long category names
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          />

          {/* Price */}
          <Box
            sx={{
              background: "linear-gradient(90deg,#F4C542 0%, #F2B537 100%)",
              color: "#082032",
              px: 2,
              py: 0.5,
              borderRadius: 2,
              fontWeight: 800,
              fontSize: 13,
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {product?.price != null &&
              !isNaN(product.price) &&
              `A$${formatPrice(product.price)}`}
          </Box>

          {/* Company Name */}
          <Box
            sx={{
              background: "rgba(59, 130, 246, 0.1)", // light blue background
              color: "#3B82F6",
              fontWeight: 600,
              fontSize: 12,
              px: 1.5,
              py: 0.5,
              borderRadius: 1.5,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {product?.seller?.company_name || "N/A"}
          </Box>

          {/* Location */}
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 0.5,
              background: "rgba(244, 196, 66, 0.15)",
              color: "#000000",
              fontWeight: 600,
              fontSize: 12,
              px: 1.5,
              py: 0.5,
              borderRadius: 1.5,
              alignSelf: "flex-start",
              // 1. Set a max-width so the container knows when to stop growing
              maxWidth: "100%",
              overflow: "hidden",
            }}
          >
            {/* 2. flexShrink: 0 prevents the icon from disappearing */}
            <FaMapPin
              style={{ flexShrink: 0, fontSize: 12, color: "#F59E42" }}
            />

            {/* 3. Wrap the text in a span that handles the ellipsis */}
            <span
              style={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                display: "block",
              }}
            >
              {capitalizeFirst(product?.location) || "Unknown"}
            </span>
          </Box>

          {/* Stock */}
          {product?.stock?.quantity > 0 ? (
            <Chip
              label={`Stock: ${product.stock.quantity}`}
              size="small"
              sx={{
                background: "linear-gradient(135deg, #3B82F6 0%, #6366F1 100%)",
                color: "#fff",
                fontSize: 12,
                px: 1,
                height: 28,
                fontWeight: 600,
              }}
            />
          ) : (
            <Chip
              label="Out of stock"
              size="small"
              sx={{
                background: "linear-gradient(135deg, #EF4444 0%, #F59E42 100%)",
                color: "#fff",
                fontSize: 12,
                px: 1,
                height: 28,
                fontWeight: 600,
              }}
            />
          )}

          {/* Verified */}
          <Chip
            icon={
              <VerifiedIcon
                color="inherit"
                sx={{ fontSize: 16, color: "#fff" }}
              />
            }
            label="Verified"
            size="small"
            sx={{
              background: "linear-gradient(135deg,#4CAF50 0%,#388E3C 100%)",
              color: "#fff",
              fontSize: 12,
              height: 28,
              fontWeight: 600,
              boxShadow: "0 6px 18px rgba(56,142,60,0.18)",
            }}
          />
        </Box>

        {/* Buttons */}
        {/* Action Buttons with Icons */}
        {!hideButtons && product?.stock?.quantity > 0 && (
          <Box sx={{ display: "flex", gap: 1 }}>
            {/* --- Bid or Buy Button --- */}
            <Tooltip
              title={product?.allow_bidding === 1 ? "Place Bid" : "Buy Now"}
              arrow
              placement="bottom"
            >
              <Button
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  product.allow_bidding === 1
                    ? setShowModal(true)
                    : setShowBuyModal(true);
                }}
                style={{
                  backgroundColor: theme.palette.secondary.main,
                  borderColor: theme.palette.secondary.main,
                  color: theme.palette.secondary.contrastText,
                  fontWeight: 700,
                  padding: 14,
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  whiteSpace: "nowrap",
                }}
              >
                {product?.allow_bidding === 1 ? (
                  <FaGavel color="#000" />
                ) : (
                  <FaShoppingCart color="#000" />
                )}
              </Button>
            </Tooltip>

            {/* --- View Details Button --- */}
            <Tooltip title="View Details" arrow placement="bottom">
              <Button
                size="sm"
                onClick={handleProductClick}
                style={{
                  backgroundColor: theme.palette.primary.main,
                  borderColor: theme.palette.primary.main,
                  color: theme.palette.primary.contrastText,
                  fontWeight: 700,
                  padding: 14,
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  whiteSpace: "nowrap",
                }}
              >
                <FaClipboardList color="#fff" />
              </Button>
            </Tooltip>
          </Box>
        )}
      </Box>
    </Box>
  );

  return (
    <>
      {viewType === "grid" ? gridView : listView}

      {/* The modals are outside the card's DOM structure. */}
      <PlaceBidModal
        show={showModal}
        onHide={() => setShowModal(false)}
        product={product}
        onSubmit={(bidData) =>
          console.log("Bid Submitted:", bidData, "for product", product)
        }
      />
      <BuyConfirmModal
        show={showBuyModal}
        onHide={() => setShowBuyModal(false)}
        product={product}
      />
    </>
  );
};

export default ProductCard;
